import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, TrendingUp, Filter } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

const scenarioConfig = {
  base: { label: "Base", color: "bg-blue-100 text-blue-800" },
  optimistic: { label: "Optimistic", color: "bg-green-100 text-green-800" },
  pessimistic: { label: "Pessimistic", color: "bg-red-100 text-red-800" }
};

const CHART_COLORS = {
  base: "hsl(153, 34%, 27%)",
  optimistic: "hsl(142, 76%, 36%)",
  pessimistic: "hsl(0, 72%, 50%)"
};

export default function ForecastsPage() {
  const { token } = useAuth();
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingForecast, setEditingForecast] = useState(null);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [scenarioFilter, setScenarioFilter] = useState("all");
  const [formData, setFormData] = useState({
    month: "",
    scenario: "base",
    revenue_forecast: "",
    expense_forecast: "",
    notes: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchForecasts();
  }, [yearFilter, scenarioFilter]);

  const fetchForecasts = async () => {
    try {
      const params = { year: yearFilter };
      if (scenarioFilter !== "all") {
        params.scenario = scenarioFilter;
      }
      const response = await axios.get(`${API}/forecasts`, { headers, params });
      setForecasts(response.data);
    } catch (error) {
      toast.error("Failed to fetch forecasts");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        revenue_forecast: parseFloat(formData.revenue_forecast) || 0,
        expense_forecast: parseFloat(formData.expense_forecast) || 0
      };
      
      if (editingForecast) {
        await axios.put(`${API}/forecasts/${editingForecast.id}`, data, { headers });
        toast.success("Forecast updated successfully");
      } else {
        await axios.post(`${API}/forecasts`, data, { headers });
        toast.success("Forecast created successfully");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchForecasts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save forecast");
    }
  };

  const handleEdit = (forecast) => {
    setEditingForecast(forecast);
    setFormData({
      month: forecast.month,
      scenario: forecast.scenario,
      revenue_forecast: forecast.revenue_forecast?.toString() || "",
      expense_forecast: forecast.expense_forecast?.toString() || "",
      notes: forecast.notes || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this forecast?")) return;
    
    try {
      await axios.delete(`${API}/forecasts/${id}`, { headers });
      toast.success("Forecast deleted successfully");
      fetchForecasts();
    } catch (error) {
      toast.error("Failed to delete forecast");
    }
  };

  const resetForm = () => {
    setEditingForecast(null);
    setFormData({
      month: "",
      scenario: "base",
      revenue_forecast: "",
      expense_forecast: "",
      notes: ""
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { 
      style: 'currency', 
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Prepare chart data
  const chartData = [];
  const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  months.forEach((m, idx) => {
    const monthKey = `${yearFilter}-${m}`;
    const baseData = forecasts.find(f => f.month === monthKey && f.scenario === 'base');
    const optimisticData = forecasts.find(f => f.month === monthKey && f.scenario === 'optimistic');
    const pessimisticData = forecasts.find(f => f.month === monthKey && f.scenario === 'pessimistic');
    
    chartData.push({
      month: monthNames[idx],
      base: baseData?.revenue_forecast || 0,
      optimistic: optimisticData?.revenue_forecast || 0,
      pessimistic: pessimisticData?.revenue_forecast || 0
    });
  });

  // Generate available years
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  return (
    <DashboardLayout title="Forecasts">
      <div className="space-y-6" data-testid="forecasts-page">
        {/* Forecast Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="section-title">Revenue Forecast by Scenario - {yearFilter}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="base" name="Base" fill={CHART_COLORS.base} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="optimistic" name="Optimistic" fill={CHART_COLORS.optimistic} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="pessimistic" name="Pessimistic" fill={CHART_COLORS.pessimistic} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-[120px]" data-testid="year-filter">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={scenarioFilter} onValueChange={setScenarioFilter}>
              <SelectTrigger className="w-[150px]" data-testid="scenario-filter">
                <SelectValue placeholder="Scenario" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Scenarios</SelectItem>
                <SelectItem value="base">Base</SelectItem>
                <SelectItem value="optimistic">Optimistic</SelectItem>
                <SelectItem value="pessimistic">Pessimistic</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-hover" data-testid="add-forecast-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Forecast
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{editingForecast ? "Edit Forecast" : "Add New Forecast"}</DialogTitle>
                <DialogDescription>
                  {editingForecast ? "Update forecast information" : "Enter the forecast details below"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="month">Month *</Label>
                    <Input
                      id="month"
                      type="month"
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      required
                      data-testid="forecast-month-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="scenario">Scenario *</Label>
                    <Select
                      value={formData.scenario}
                      onValueChange={(value) => setFormData({ ...formData, scenario: value })}
                    >
                      <SelectTrigger data-testid="forecast-scenario-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="base">Base</SelectItem>
                        <SelectItem value="optimistic">Optimistic</SelectItem>
                        <SelectItem value="pessimistic">Pessimistic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="revenue_forecast">Revenue Forecast (AUD) *</Label>
                    <Input
                      id="revenue_forecast"
                      type="number"
                      step="0.01"
                      value={formData.revenue_forecast}
                      onChange={(e) => setFormData({ ...formData, revenue_forecast: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="forecast-revenue-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expense_forecast">Expense Forecast (AUD) *</Label>
                    <Input
                      id="expense_forecast"
                      type="number"
                      step="0.01"
                      value={formData.expense_forecast}
                      onChange={(e) => setFormData({ ...formData, expense_forecast: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="forecast-expense-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Assumptions and notes..."
                    rows={2}
                    data-testid="forecast-notes-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-forecast-btn">
                    {editingForecast ? "Update" : "Add"} Forecast
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Forecasts Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-muted-foreground">Loading forecasts...</div>
              </div>
            ) : forecasts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <TrendingUp className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No forecasts yet</h3>
                <p className="text-muted-foreground mt-1">Add your first forecast to start planning</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header">Month</TableHead>
                    <TableHead className="table-header">Scenario</TableHead>
                    <TableHead className="table-header text-right">Revenue Forecast</TableHead>
                    <TableHead className="table-header text-right">Expense Forecast</TableHead>
                    <TableHead className="table-header text-right">Net Forecast</TableHead>
                    <TableHead className="table-header">Notes</TableHead>
                    <TableHead className="table-header text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {forecasts.map((forecast) => (
                    <TableRow key={forecast.id} className="hover:bg-accent/50 transition-colors">
                      <TableCell className="data-cell font-medium">{forecast.month}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={scenarioConfig[forecast.scenario]?.color}>
                          {scenarioConfig[forecast.scenario]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right data-cell positive-value">
                        {formatCurrency(forecast.revenue_forecast)}
                      </TableCell>
                      <TableCell className="text-right data-cell negative-value">
                        {formatCurrency(forecast.expense_forecast)}
                      </TableCell>
                      <TableCell className={`text-right data-cell font-medium ${
                        forecast.revenue_forecast - forecast.expense_forecast >= 0 ? 'positive-value' : 'negative-value'
                      }`}>
                        {formatCurrency(forecast.revenue_forecast - forecast.expense_forecast)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {forecast.notes || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(forecast)}
                            data-testid={`edit-forecast-${forecast.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(forecast.id)}
                            data-testid={`delete-forecast-${forecast.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
